<template>
  <main class="all-deals">
    <!-- Page Header -->
    <div class="page-header">
      <div class="page-header__text">
        <h1 class="page-header__title">All Deals</h1>
        <p class="page-header__description" v-if="tokenRequests">
          You have {{ convertToLocale(tokenRequests.length) }}
          {{ tokenRequests.length > 1 ? "deals" : "deal" }}. Click on each deal to see more information.
        </p>
      </div>
      <button class="btn btn--primary" @click="checkDeals">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Create New Deal
      </button>
    </div>

    <!-- Search -->
    <div class="search-wrapper">
      <Search
        type="name"
        :showSearchField="true"
        placeholder="Search deals..."
        @search-input="findDeal"
      />
    </div>

    <!-- Deals Grid -->
    <section class="deals-grid" v-if="tokenRequests && tokenRequests.length > 0">
      <DealCard v-for="token in tokenRequests" :key="token._id" :deal="token" />
    </section>

    <!-- Empty State -->
    <div class="empty-state" v-else>
      <div class="empty-state__icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3 9H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 21V9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h3 class="empty-state__title">
        {{ searchValue.length > 0 ? "No results found" : "No deals yet" }}
      </h3>
      <p class="empty-state__description">
        {{ searchValue.length > 0 ? "Try a different search term" : "Create your first deal to start raising capital" }}
      </p>
      <router-link
        v-if="searchValue.length === 0"
        :to="{ name: 'CreateAsset', params: { type: 'private-raise' } }"
      >
        <button class="btn btn--primary" v-can.disable="'deal:addDeal'">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Create a Deal
        </button>
      </router-link>
    </div>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Search from "@/components/general/Search";
import search from "@/mixins/search";
import { convertToLocale } from "@/utils/helpers";
import Pagination from "@/components/navigation/Pagination";
import DealCard from "@/components/privateRaise/DealCard";
import EmptyState from "@/components/notification/EmptyState";
export default {
  name: "AllDeals",
  data() {
    return {
      searchValue: "",
      canCreate: false,
    };
  },
  components: {
    Search,
    DealCard,
    Pagination,
    EmptyState,
  },
  computed: {
    ...mapGetters("TokenModule", ["tokenRequests"]),
  },
  mixins: [search],
  created() {
    this.fetchTokens();
  },
  methods: {
    ...mapActions("TokenModule", ["getTokenRequests", "getDealByName"]),
    ...mapActions(["showToast"]),
    convertToLocale,
    async fetchTokens() {
      try {
        const response = await this.getTokenRequests();
        const canCreate = response.requests.filter(
          (request) => request.status !== "Approved"
        );
        this.canCreate = canCreate.length === 0;
      } catch (error) {
        return error;
      }
    },
    async findDeal({ value }) {
      this.searchValue = value;
      try {
        await this.getDealByName(value);
      } catch (error) {
        return error;
      }
    },
    checkDeals() {
      if (this.canCreate) {
        this.$router.push({
          name: "CreateAsset",
          params: { type: "private-raise" },
        });
      } else {
        this.showToast({
          description:
            "You need to complete your previous deal before you can create another one",
          display: true,
          type: "error",
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.all-deals {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
  
  &__title {
    font-size: 24px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 6px;
  }
  
  &__description {
    font-size: 14px;
    color: #64748B;
    font-weight: 400;
  }
}

.search-wrapper {
  margin-bottom: 24px;
  max-width: 320px;
}

.deals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
  
  &__icon {
    width: 80px;
    height: 80px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    margin-bottom: 24px;
  }
  
  &__title {
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }
  
  &__description {
    font-size: 14px;
    color: #64748B;
    max-width: 320px;
    line-height: 1.6;
    margin-bottom: 24px;
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  
  &--primary {
    padding: 12px 20px;
    background: #4D84FF;
    color: #ffffff;
    
    &:hover {
      background: #3B6EE8;
    }
  }
}
</style>
